import React, { useEffect, useRef, useState } from "react";

/**
 * Champ adresse avec autocomplétion via la Base Adresse Nationale (BAN) —
 * api-adresse.data.gouv.fr, gratuite et SANS clé. En tapant le début d'une
 * adresse, des suggestions apparaissent ; en choisir une remplit d'un coup
 * l'adresse, le code postal et la ville (via onSelect).
 *
 * Props :
 *  - value          : texte de l'adresse (champ rue), contrôlé
 *  - onChange(v)     : frappe libre (ne touche QUE la rue)
 *  - onSelect({address, postalCode, city}) : sélection d'une suggestion
 *  - placeholder, inputClassName, label, testid
 */
const BAN_URL = "https://api-adresse.data.gouv.fr/search/";

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  inputClassName = "",
  label,
  labelClassName,
  testid,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = (value || "").trim();
    if (timer.current) clearTimeout(timer.current);
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${BAN_URL}?q=${encodeURIComponent(q)}&limit=5`);
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen((data.features || []).length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [value]);

  useEffect(() => {
    const onClickOut = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  const choose = (f) => {
    const p = f.properties || {};
    onSelect?.({
      address: p.name || "",
      postalCode: p.postcode || "",
      city: p.city || "",
    });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className={labelClassName}>{label}</label>}
      <input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
        className={inputClassName}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          {suggestions.map((f) => (
            <li
              key={f.properties.id}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(f);
              }}
              className="px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer"
            >
              {f.properties.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
